/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

// Matches Hygraph's default CDNs and Epic's CNAME aliases (staging + prod), so that feed
// API responses that return prod URLs are still recognised in staging/local dev.
const HYGRAPH_HOST_PATTERNS = [/\.graphassets\.com$/, /(^|\.)cms-assets(-[\w-]+)?\.unrealengine\.com$/];

/**
 * Determines if a given src URL is a hygraph asset
 * @param hygraphMediaDomain
 * @param src
 * @returns boolean
 */
export const isHygraphImageSrc = (hygraphMediaDomain: string, src: string): boolean => {
	if (!src) return false;
	if (hygraphMediaDomain && src.includes(hygraphMediaDomain)) return true;
	// src may be a full URL or a bare hostname (getHygraphImageIds calls this with parts[2])
	let host = src;
	try {
		host = new URL(src).hostname;
	} catch {
		// leave host as the raw src (likely already a hostname)
	}
	return HYGRAPH_HOST_PATTERNS.some((re) => re.test(host));
};

interface HygraphImageIds {
	assetId: string;
	environmentId?: string;
}

/**
 * Attempts to parse hygraph asset URL and return the assetId, environmentId
 */
export const getHygraphImageIds = (hygraphMediaDomain: string, src: string): HygraphImageIds => {
	let assetId = "";
	let environmentId = "";

	if (!src) {
		return { assetId, environmentId };
	}

	// strip query string before parsing so suffixes like ?width=N don't mask the trailing id segment
	const pathOnly = src.split("?")[0] || src;
	const parts = pathOnly.split("/");

	if (parts.length && parts[2] && isHygraphImageSrc(hygraphMediaDomain, parts[2]) && parts.length > 3) {
		assetId = parts[3];

		//new system
		if (parts.length >= 5) {
			// remove output= resize=x: parts
			const cleanedIds = parts
				.slice(4)
				.filter((el) => el && !el.includes(":") && !el.includes("=") && el !== "auto_image");
			if (cleanedIds.length > 0) environmentId = cleanedIds[0];
		}
	}

	return { assetId, environmentId };
};

// Markers that mean the URL already requests format/quality optimization, so
// adding compression would be redundant. `resize=` is deliberately NOT here — it
// is a geometry transform, and a resized-but-uncompressed image (the common
// shape CKEditor emits from its image resize handles) still needs compression
// added while its resize segment is preserved.
const HYGRAPH_COMPRESSION_MARKERS = ["quality=value", "output=format", "auto_image"];

/**
 * Whether a URL string already requests Hygraph format/quality optimization and
 * therefore needs no added compression. This is a bare substring check — it does
 * not validate that `src` is a Hygraph asset URL; pair it with
 * {@link isHygraphImageSrc}.
 */
export const hasHygraphCompression = (src: string): boolean => {
	return HYGRAPH_COMPRESSION_MARKERS.some((marker) => src.includes(marker));
};

// Hygraph environment ids and asset handles are URL-safe alphanumerics; transform
// segments instead carry `=` (e.g. `resize=height:80`). Used to tell them apart.
const HYGRAPH_ID_SHAPE = /^[A-Za-z0-9_-]+$/;

/**
 * Builds a compressed variant (`quality=value:90/auto_image`) of a Hygraph asset
 * URL, mirroring the path-transform shape of `hygraphImageLoader`'s no-width
 * branch (without the `?width=N` suffix next/image requires). Existing geometry
 * transforms (e.g. `resize=height:80`) are preserved and the compression
 * transforms are inserted just before the asset handle. The query string is
 * preserved (as serialized by `URL` — verbatim for already-encoded queries) and
 * the returned URL is always `https`. No `resize=` width variant is added, since
 * the rendered width is unknown at this level.
 *
 * Returns `""` (caller keeps the original) when the src can't be safely
 * rewritten: a non-Hygraph host, a URL that already carries compression/format
 * transforms, a `/process` endpoint (different grammar), a fragment-bearing src
 * (`&#38;`-style entities in attribute values parse as fragments), a legacy
 * single-segment `/{handle}` URL (transforms-first grammar the rewritten shape
 * 404s on), or any path whose first/last segment isn't a bare id or whose middle
 * segments aren't all transforms (carry `=`).
 *
 * Accepts protocol-relative (`//host/...`) srcs by assuming https. The origin
 * host is preserved rather than forced to the configured domain — CMS content
 * may carry prod URLs in staging/local envs.
 *
 * @param hygraphMediaDomain - The configured Hygraph media CDN domain. Built-in
 *        Hygraph host patterns are matched even when this is empty.
 * @param src - The image src URL to rewrite.
 * @returns The compressed URL, or `""` when the src can't be safely rewritten.
 */
export const buildHygraphCompressedImageUrl = (hygraphMediaDomain: string, src: string): string => {
	if (!src || src.includes("#")) return "";

	const absoluteSrc = src.startsWith("//") ? `https:${src}` : src;
	if (!isHygraphImageSrc(hygraphMediaDomain, absoluteSrc)) return "";
	if (hasHygraphCompression(absoluteSrc)) return "";

	let url: URL;
	try {
		url = new URL(absoluteSrc);
	} catch {
		return "";
	}

	// The first isHygraphImageSrc call matches the configured domain anywhere in
	// the raw src (including query params) — re-check the parsed hostname so a
	// foreign host can't smuggle the domain through its query string.
	if (!isHygraphImageSrc(hygraphMediaDomain, url.hostname)) return "";

	const pathSegments = url.pathname.split("/").filter(Boolean);
	if (pathSegments.length < 2 || pathSegments.includes("process")) return "";

	const environmentId = pathSegments[0];
	const assetHandle = pathSegments[pathSegments.length - 1];
	const transforms = pathSegments.slice(1, -1);

	// First (env) and last (handle) must be bare ids; every middle segment must be
	// a transform (carries `=`). A bare-id middle segment is an unfamiliar 3-id
	// shape we won't risk rewriting.
	if (!HYGRAPH_ID_SHAPE.test(environmentId) || !HYGRAPH_ID_SHAPE.test(assetHandle)) return "";
	if (transforms.some((segment) => !segment.includes("="))) return "";

	const existingTransforms = transforms.length ? `${transforms.join("/")}/` : "";
	return `https://${url.hostname}/${environmentId}/${existingTransforms}quality=value:90/auto_image/${assetHandle}${url.search}`;
};
